import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { BarChart, Bar, ResponsiveContainer, XAxis, Tooltip } from "recharts";
import { ExternalLink, ToggleLeft, Info } from "lucide-react";
import { api } from "@/lib/api";
import { FullSpinner } from "@/components/States";

const tip = { background: "#1A090D", border: "1px solid #781826", borderRadius: 12, color: "#FAF5F0", fontSize: 12 };
const APP_ORIGIN = process.env.REACT_APP_BACKEND_URL;

export default function AdminFeatureWorkspace() {
  const { key } = useParams();
  const [d, setD] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    setD(null); setErr(null);
    api.get(`/admin/feature/${key}/analytics`).then(({ data }) => setD(data)).catch(() => setErr("Unable to load this workspace."));
  }, [key]);

  if (err) return <div className="text-sm text-rose-300" data-testid="feature-error">{err}</div>;
  if (!d) return <FullSpinner label="Loading workspace…" />;
  const f = d.feature;

  return (
    <div className="space-y-6" data-testid={`admin-feature-${key}`}>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="font-serif text-3xl font-bold">{f.name}</h1>
          {f.desc && <p className="text-sm text-[#D8B4B9] mt-1 max-w-2xl">{f.desc}</p>}
        </div>
        {f.user_route && (
          <a href={`${APP_ORIGIN}${f.user_route}`} target="_blank" rel="noreferrer"
            className="flex items-center gap-1.5 text-xs text-[#C59B27] border border-[#781826]/50 rounded-lg px-3 py-2 hover:bg-[#2A0F14]" data-testid="open-user-feature">
            <ExternalLink className="w-3.5 h-3.5" /> Open member view
          </a>
        )}
      </div>

      {f.flag && d.flag && (
        <a href="/admin/feature-flags" className="flex items-center gap-2 text-xs bg-[#2A0F14] border border-[#781826]/40 rounded-xl px-3 py-2 w-fit">
          <ToggleLeft className="w-4 h-4 text-[#C59B27]" />
          Feature flag <span className="font-mono text-[#E2C382]">{f.flag}</span> is
          <span className={d.flag.enabled ? "text-emerald-300" : "text-rose-300"}>{d.flag.enabled ? "enabled" : "disabled"}</span>
          · manage →
        </a>
      )}

      {/* Metrics */}
      <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-5 gap-3">
        {d.metrics.map((m) => (
          <div key={m.label} className="bg-[#2A0F14] border border-[#781826]/40 rounded-2xl p-4" data-testid={`metric-${m.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
            <div className="font-serif text-2xl font-bold text-[#E2C382]">{m.value}</div>
            <div className="text-xs text-[#D8B4B9] mt-0.5">{m.label}</div>
            {m.hint && <div className="text-[10px] text-[#8A6D71] mt-1">{m.hint}</div>}
          </div>
        ))}
      </div>

      {d.series && d.series.length > 0 && (
        <div className="bg-[#2A0F14] border border-[#781826]/40 rounded-2xl p-5">
          <h2 className="font-serif text-lg font-semibold mb-4">Trend</h2>
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={d.series}>
              <XAxis dataKey="date" stroke="#8A6D71" fontSize={11} />
              <Tooltip contentStyle={tip} />
              <Bar dataKey="count" fill="#C59B27" radius={[5, 5, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}

      {d.recent && d.recent.length > 0 && (
        <div className="bg-[#2A0F14] border border-[#781826]/40 rounded-2xl p-5">
          <h2 className="font-serif text-lg font-semibold mb-3">Recent</h2>
          <div className="divide-y divide-[#781826]/20">
            {d.recent.map((r, i) => {
              const inner = (
                <div className="flex items-center justify-between py-2.5 text-sm">
                  <div className="min-w-0"><div className="text-[#FAF5F0] truncate capitalize">{r.title}</div>{r.sub && <div className="text-[11px] text-[#8A6D71] font-mono truncate">{r.sub}</div>}</div>
                  <div className="text-[11px] text-[#8A6D71] shrink-0 ml-3">{r.at ? new Date(r.at).toLocaleString() : ""}</div>
                </div>
              );
              return r.link ? <a key={i} href={r.link} className="block hover:bg-[#1A090D] rounded-lg px-2 -mx-2">{inner}</a> : <div key={i}>{inner}</div>;
            })}
          </div>
        </div>
      )}

      {d.note && (
        <div className="flex items-start gap-2 text-xs text-[#D8B4B9] bg-[#1A090D] border border-[#781826]/30 rounded-xl px-3 py-2.5">
          <Info className="w-4 h-4 text-[#C59B27] shrink-0 mt-0.5" /> {d.note}
        </div>
      )}
    </div>
  );
}
